import {
  Settings,
  ChartPie,
  Users,
  Building2,
  UserCog,
  Integrations,
  Lightning,
  GearSix,
  BookOpenText,
  ChartLineUp,
  TestTube,
  TerminalIcon,
  Star,
  ClipboardText,
  Wrench,
  TreeStructure,
  FileText,
  ArticleIcon,
} from '@/lib/icons';

export interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface NavigationSection {
  label: string;
  items: NavigationItem[];
}

export type NavigationGroupEntry = NavigationItem | NavigationSection;

export interface NavigationGroup {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  entries: NavigationGroupEntry[];
}

export type NavigationEntry = NavigationItem | NavigationGroup;

export function isNavigationGroup(entry: NavigationEntry): entry is NavigationGroup {
  return 'entries' in entry;
}

export function isNavigationSection(entry: NavigationGroupEntry): entry is NavigationSection {
  return 'label' in entry;
}

const baseNavigation: NavigationItem[] = [
  { name: 'Users', href: '/users', icon: Users },
  { name: 'Account Settings', href: '/settings', icon: Settings },
  { name: 'Reports', href: '/reports', icon: ChartPie },
  { name: 'Integrations', href: '/integrations', icon: Integrations },
];

// Agent Settings and Prompts are super-only; everything else in the group is
// available to the whole Kaiya section (super, admin, affiliate_admin).
function buildKaiyaNavigationGroup(isSuperAdmin: boolean): NavigationGroup {
  const configurationItems: NavigationItem[] = [
    { name: 'Domains', href: '/kaiya-domains', icon: TreeStructure },
    ...(isSuperAdmin
      ? [
          { name: 'Agent Settings', href: '/kaiya-settings', icon: GearSix },
          { name: 'Prompts', href: '/kaiya-prompts', icon: FileText },
        ]
      : []),
  ];

  return {
    name: 'Kaiya',
    icon: Lightning,
    entries: [
      { name: 'Dashboard', href: '/kaiya-dashboard', icon: ChartLineUp },
      {
        label: 'Content',
        items: [
          { name: 'Skills', href: '/kaiya-skills', icon: Wrench },
          { name: 'Golden Docs', href: '/kaiya-golden-docs', icon: Star },
          { name: 'Guided Use Cases', href: '/kaiya-guided-use-cases', icon: ArticleIcon },
          { name: 'Terminology', href: '/kaiya-terminology', icon: BookOpenText },
        ],
      },
      {
        label: 'Configuration',
        items: configurationItems,
      },
      {
        label: 'Quality & Testing',
        items: [
          { name: 'Evals', href: '/kaiya-evals', icon: TestTube },
          { name: 'Review Queue', href: '/kaiya-review-queue', icon: ClipboardText },
          { name: 'Playground', href: '/kaiya-playground', icon: TerminalIcon },
        ],
      },
    ],
  };
}

const superAdminNavigation: NavigationItem[] = [
  { name: 'Admin Users', href: '/admin-users', icon: UserCog },
];

export interface NavigationConfig {
  isSuperAdmin: boolean;
  isProsciAdmin: boolean;
  isLicenseAdmin: boolean;
  isDataLevel: boolean;
  isAffiliate: boolean;
  canAccessKaiya: boolean;
}

export function getNavigationItems(config: NavigationConfig): NavigationEntry[] {
  const { isProsciAdmin, isSuperAdmin, isAffiliate, canAccessKaiya } = config;

  let navigation: NavigationEntry[] = [...baseNavigation];

  if (isProsciAdmin) {
    // Remove Account Settings and Integrations for Prosci admins (only visible to License Admins)
    navigation = navigation.filter(
      (item) => item.name !== 'Account Settings' && item.name !== 'Integrations',
    );

    // Add Accounts after Users - but NOT for affiliate users
    if (!isAffiliate) {
      const usersIndex = navigation.findIndex(
        (item) => !isNavigationGroup(item) && item.name === 'Users'
      );
      if (usersIndex !== -1) {
        navigation = [
          ...navigation.slice(0, usersIndex + 1),
          { name: 'Accounts', href: '/accounts', icon: Building2 },
          ...navigation.slice(usersIndex + 1),
        ];
      }
    }

    // Add Kaiya group only for section-eligible admins (super, admin, affiliate_admin)
    if (canAccessKaiya) {
      navigation = [...navigation, buildKaiyaNavigationGroup(isSuperAdmin)];
    }

    // Add super admin items if applicable
    if (isSuperAdmin) {
      navigation = [...navigation, ...superAdminNavigation];
    }
  }

  return navigation;
}
